import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Search, SlidersHorizontal, Plus, X, CheckSquare, Square, Trash2,
  Archive, Clock, Plane, CalendarDays, Building2, UserCheck, UserX,
  ChevronLeft, ChevronRight, Globe2, ShieldCheck, ShieldAlert, ShieldOff, Loader2,
  QrCode,
} from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { api } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useBreakpoint } from "@/lib/useBreakpoint";
import { WS_TOKENS } from "@/lib/workspaceTheme";
import { fmtDate, fmtNum, statusLabel } from "@/lib/format";
import { localizeCountry } from "@/lib/locales";

/**
 * ApplicationsList v3 — Apple HIG inspired.
 *
 * Design language:
 *   • Large bold title + soft sub-count (SwiftUI Nav-style).
 *   • Compact segmented tab bar (iOS UISegmentedControl feel).
 *   • Search field with sf-style rounded look and inline icons.
 *   • Rows grouped in a single elevated card with hair-line dividers.
 *   • Each row: colored avatar chip, name+meta, semantic pill chips.
 *   • Freshness glowing dot + smooth cascading fade-in on mount.
 *   • Left accent hair-line only when meaningful (late / warning / fresh).
 */
export default function ApplicationsList() {
  const { theme, dir, lang } = useI18n();
  const { user } = useAuth();
  const { isDesktop } = useBreakpoint();
  const isRoot = !!user?.role?.is_root;
  const isEmployee = user?.role?.code === "employee";
  const canCreate = isRoot || ["employee", "general_manager", "deputy_manager"].includes(user?.role?.code);
  const canSeeObserver = isRoot || ["general_manager", "deputy_manager"].includes(user?.role?.code); // Monitoring is management-only
  const canSeeArchive = isRoot;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [busyBulk, setBusyBulk] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);

  // ── URL is the single source of truth for `view` and `archived` — no local
  // state, no sync effects, no flash on navigation between sidebar links. ──
  const view = searchParams.get("view") || "action_required";
  const archived = searchParams.get("archived") === "1";

  const setView = useCallback((next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "action_required") params.delete("view"); else params.set("view", next);
    params.delete("archived");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const setArchived = useCallback((flag) => {
    const params = new URLSearchParams(searchParams);
    if (flag) { params.set("archived", "1"); params.delete("view"); }
    else params.delete("archived");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const apiView = ["action_required", "all"].includes(view) ? view : "all";
      const { data } = await api.get("/workspace/applications", {
        params: { search: search || undefined, archived, view: apiView, limit: 200 },
      });
      let items = data.items || [];
      let total = data.total || 0;
      const nowMs = Date.now();
      if (view === "travel_soon") {
        items = items.filter((a) => {
          if (!a.appointment_date) return false;
          const days = Math.round((new Date(a.appointment_date).getTime() - nowMs) / 86400000);
          return days <= 10;
        }).sort((a, b) => new Date(a.appointment_date) - new Date(b.travel_date));
        total = items.length;
      } else if (view === "follow_up") {
        // "Follow-up" = travel within 10 days OR SLA breached (late)
        items = items.filter((a) => {
          const isLate = a.sla_state === "breached";
          if (isLate) return true;
          if (!a.appointment_date) return false;
          const days = Math.round((new Date(a.appointment_date).getTime() - nowMs) / 86400000);
          return days <= 10 && days >= -1;
        });
        total = items.length;
      } else if (view === "invoices") {
        // Invoices tab = every application whose payment has been requested,
        // collected, or fully settled. Includes BOTH the client-portal path
        // (fees_set → payment_uploaded → payment_approved → processing →
        // appointment → completed) AND the office-internal path
        // (payment_recorded → receipt_issued → with_official_entity →
        // official_entity_approved → appointment_uploaded → office_completed).
        // Anything before pricing (submission, under_review, office_intake)
        // is excluded — it doesn't belong in the invoices ledger yet.
        const INV_STATUSES = new Set([
          // Portal workflow (pricing → payment → completion)
          "fees_set", "awaiting_payment", "payment_uploaded", "payment_approved",
          "processing", "appointment", "appointment_booked", "completed",
          // Internal (office) workflow (payment collected on the spot → done)
          "payment_recorded", "receipt_issued",
          "with_official_entity", "official_entity_approved",
          "appointment_uploaded", "office_completed",
        ]);
        items = items.filter((a) => INV_STATUSES.has((a.current_status_code || "").toLowerCase()));
        total = items.length;
      } else if (view === "completed") {
        items = items.filter((a) => (a.current_status_code || "").toLowerCase() === "completed");
        total = items.length;
      } else if (view === "new") {
        items = items.filter((a) => !!a.is_new);
        total = items.length;
      }
      setItems(items);
      setTotal(total);
    } catch (e) {
      setErr(e?.response?.data?.detail || (lang === "ar" ? "تعذّر التحميل" : "Failed to load"));
    } finally { setLoading(false); }
  }, [search, archived, view, lang]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const T = lang === "ar"
    ? {
        title: "المعاملات",
        title_invoices: "الفواتير",
        title_archive: "الأرشيف",
        title_follow: "المتابعة",
        search_ph: "ابحث برقم المعاملة، الاسم، الجوال، الجواز…",
        empty_action: "لا توجد معاملات تتطلب فعلك حالياً",
        empty_all: "لا توجد معاملات",
        empty_follow: "لا يوجد ما يحتاج متابعة الآن",
        empty_invoices: "لا توجد فواتير حالية",
        empty_archive: "الأرشيف فارغ",
        tab_action: "تتطلب إجراء",
        tab_observer: "للمراقبة",
        tab_travel: "موعد قريب",
        tab_new: "جديدة",
        tab_completed: "منجزة",
        tab_archived: "المؤرشفة",
        tab_follow: "المتابعة",
        tab_invoices: "الفواتير",
        select_mode: "تحديد",
        cancel_select: "إلغاء",
        selected: (n) => `${fmtNum(n)} محدّدة`,
        delete_btn: "حذف نهائي",
        archive_btn: "أرشفة",
        restore_btn: "استرجاع",
        confirm_delete: (n) => `هل تريد حذف ${fmtNum(n)} معاملة نهائياً؟`,
        confirm_archive: (n) => `أرشفة ${fmtNum(n)} معاملة؟`,
        select_all: "تحديد الكل",
        deselect_all: "إلغاء الكل",
        new_app: "معاملة جديدة",
        unassigned: "غير مُسندة",
        source_portal: "من البوابة",
        source_office: "داخلي",
        count: (n) => `${fmtNum(n)} معاملة`,
        late: "متأخرة",
        today: "اليوم",
      }
    : {
        title: "Applications",
        title_invoices: "Invoices",
        title_archive: "Archive",
        title_follow: "Follow-up",
        search_ph: "Search by number, name, mobile, passport…",
        empty_action: "Nothing currently requires your action",
        empty_all: "No applications",
        empty_follow: "Nothing needs follow-up right now",
        empty_invoices: "No active invoices",
        empty_archive: "Archive is empty",
        tab_action: "Action required",
        tab_observer: "Observer",
        tab_travel: "Travel soon",
        tab_new: "New",
        tab_completed: "Completed",
        tab_archived: "Archived",
        tab_follow: "Follow-up",
        tab_invoices: "Invoices",
        select_mode: "Select",
        cancel_select: "Cancel",
        selected: (n) => `${fmtNum(n)} selected`,
        delete_btn: "Delete",
        archive_btn: "Archive",
        restore_btn: "Restore",
        confirm_delete: (n) => `Delete ${fmtNum(n)} application(s) permanently?`,
        confirm_archive: (n) => `Archive ${fmtNum(n)} application(s)?`,
        select_all: "Select all",
        deselect_all: "Deselect all",
        new_app: "New application",
        unassigned: "Unassigned",
        source_portal: "Portal",
        source_office: "Office",
        count: (n) => `${fmtNum(n)} item${n === 1 ? "" : "s"}`,
        late: "Late",
        today: "Today",
      };

  const tabs = useMemo(() => {
    // Special-purpose views are shown as a single "focused" title (no tab bar noise).
    // The user still gets standard tabs when browsing normal Applications.
    if (view === "invoices" || archived || view === "follow_up") return null;

    const arr = [{ k: "action_required", label: T.tab_action }];
    if (canSeeObserver) arr.push({ k: "all", label: T.tab_observer });
    arr.push({ k: "travel_soon", label: T.tab_travel });
    arr.push({ k: "new", label: T.tab_new });
    arr.push({ k: "completed", label: T.tab_completed });
    return arr;
  }, [T, canSeeObserver, view, archived]);

  // Page title reflects the focused view
  const pageTitle = archived
    ? T.title_archive
    : view === "invoices" ? T.title_invoices
    : view === "follow_up" ? T.title_follow
    : T.title;
  const emptyMessage = archived
    ? T.empty_archive
    : view === "invoices" ? T.empty_invoices
    : view === "follow_up" ? T.empty_follow
    : view === "action_required" ? T.empty_action
    : T.empty_all;

  const allIds = useMemo(() => items.map((a) => a.id), [items]);
  const toggle = (id) => setSelected((s) => { const c = new Set(s); c.has(id) ? c.delete(id) : c.add(id); return c; });
  const toggleAll = () => setSelected((s) => s.size === allIds.length ? new Set() : new Set(allIds));
  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); };

  const doBulk = async (action) => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    if (action === "delete" && !window.confirm(T.confirm_delete(ids.length))) return;
    if (action === "archive" && !window.confirm(T.confirm_archive(ids.length))) return;
    setBusyBulk(true);
    try {
      const path = action === "delete" ? "bulk-delete"
        : action === "archive" ? "bulk-archive"
        : "bulk-restore";
      await api.post(`/workspace/applications/${path}`, { ids });
      exitSelect();
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Bulk operation failed");
    } finally { setBusyBulk(false); }
  };

  const css = {
    page: {
      minHeight: "100%",
      background: theme.bg,
      color: theme.text,
      fontFamily: "'Tajawal','Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
      padding: `${isDesktop ? 28 : 16}px ${isDesktop ? 36 : 16}px ${isDesktop ? 40 : 32}px`,
      maxWidth: 1280, margin: "0 auto",
    },
    // ── Large SwiftUI-style header ────────────────────────────────
    header: {
      display: "flex", alignItems: "flex-end", justifyContent: "space-between",
      gap: 16, marginBottom: 20, flexWrap: "wrap",
    },
    titleWrap: { display: "flex", flexDirection: "column", gap: 4 },
    title: {
      fontSize: isDesktop ? 32 : 26, fontWeight: 800,
      color: theme.text, letterSpacing: "-0.4px", lineHeight: 1.1,
    },
    subCount: { fontSize: 13, color: theme.textMuted, fontWeight: 600 },
    newBtn: {
      background: theme.goldGradient, color: "#0A0F19", border: "none",
      borderRadius: 999, padding: "10px 18px",
      fontWeight: 800, fontSize: 13, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 6,
      minHeight: 40, fontFamily: "inherit",
      boxShadow: "0 4px 12px rgba(212,175,55,0.25)",
      transition: `transform ${WS_TOKENS.motion.fast}, box-shadow ${WS_TOKENS.motion.fast}`,
    },
    // ── Segmented control (iOS style) ─────────────────────────────
    segWrap: {
      display: "flex", gap: 2, marginBottom: 14, padding: 3,
      background: theme.surfaceAlt,
      border: `1px solid ${theme.borderSoft}`,
      borderRadius: 12,
      overflowX: "auto", WebkitOverflowScrolling: "touch",
      flexWrap: isDesktop ? "wrap" : "nowrap",
    },
    seg: (active) => ({
      padding: isDesktop ? "8px 16px" : "8px 12px",
      borderRadius: 9,
      background: active ? theme.surface : "transparent",
      color: active ? theme.text : theme.textMuted,
      border: "none",
      fontSize: 12.5, fontWeight: active ? 800 : 600, cursor: "pointer",
      whiteSpace: "nowrap",
      fontFamily: "inherit",
      minHeight: 34,
      transition: `background ${WS_TOKENS.motion.fast}, color ${WS_TOKENS.motion.fast}`,
      boxShadow: active ? "0 1px 2px rgba(0,0,0,0.12)" : "none",
    }),
    // ── Search + tool row ─────────────────────────────────────────
    toolbar: { display: "flex", gap: 8, marginBottom: 16, alignItems: "center" },
    searchWrap: { flex: "1 1 auto", position: "relative", display: "flex", alignItems: "center" },
    searchIcon: { position: "absolute", insetInlineStart: 14, color: theme.textMuted, pointerEvents: "none" },
    search: {
      width: "100%", padding: "11px 16px", paddingInlineStart: 42,
      borderRadius: 12,
      background: theme.inputBg, border: `1px solid ${theme.inputBorder}`,
      color: theme.text, fontSize: 14, fontFamily: "inherit",
      boxSizing: "border-box", minHeight: 44,
      outline: "none",
      transition: `border-color ${WS_TOKENS.motion.fast}, box-shadow ${WS_TOKENS.motion.fast}`,
    },
    iconBtn: (active) => ({
      width: 44, height: 44,
      borderRadius: 12,
      background: active ? "rgba(212,175,55,0.14)" : "transparent",
      border: `1px solid ${active ? theme.borderAccent : theme.border}`,
      color: active ? theme.gold : theme.text,
      cursor: "pointer",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      transition: `background ${WS_TOKENS.motion.fast}`,
    }),
    // ── List card container (Apple grouped list feel) ─────────────
    listCard: {
      background: theme.surface,
      border: `1px solid ${theme.borderSoft}`,
      borderRadius: 16,
      overflow: "hidden",
      boxShadow: theme.cardShadow,
    },
    empty: { padding: 60, textAlign: "center", color: theme.textMuted, fontSize: 13 },
    err: {
      padding: 12, background: theme.errorBg, border: `1px solid ${theme.errorBorder}`,
      color: theme.errorText, borderRadius: 12, marginBottom: 12, fontSize: 13,
    },
    bulkBar: {
      display: "flex", gap: 8, padding: 12, marginBottom: 12,
      background: theme.surface, border: `1px solid ${theme.borderSoft}`,
      borderRadius: 12,
      alignItems: "center", flexWrap: "wrap",
    },
    bulkCount: { fontSize: 12, fontWeight: 800, color: theme.gold },
    bulkBtnGhost: {
      padding: "8px 12px", border: `1px solid ${theme.border}`, background: "transparent",
      color: theme.text, borderRadius: 10, cursor: "pointer",
      fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 5,
      fontFamily: "inherit",
    },
    bulkBtnDanger: {
      padding: "8px 12px", border: "1px solid rgba(220,38,38,0.35)",
      background: "rgba(220,38,38,0.10)", color: theme.danger,
      borderRadius: 10, cursor: "pointer",
      fontSize: 12, fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 5,
      fontFamily: "inherit",
    },
    skRow: {
      height: 78,
      borderBottom: `1px solid ${theme.borderSoft}`,
      background: `linear-gradient(90deg, transparent, ${theme.borderSoft}, transparent)`,
      backgroundSize: "200% 100%",
      animation: "ws-shimmer 1.4s infinite",
    },
  };

  return (
    <div dir={dir} style={css.page} data-testid="apps-list-page">
      <style>{`
        @keyframes ws-shimmer { 0%{background-position:0 0} 100%{background-position:-200% 0} }
        @keyframes ws-fresh-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(22,163,74, 0.55); }
          50%      { box-shadow: 0 0 0 6px rgba(22,163,74, 0); }
        }
        @keyframes ws-row-in {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .ws-search:focus {
          border-color: ${theme.borderAccent} !important;
          box-shadow: 0 0 0 4px rgba(212,175,55,0.10) !important;
        }
        .ws-new-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 18px rgba(212,175,55,0.35); }
      `}</style>

      {/* ── Large title + count + primary CTA ─────────────────── */}
      <div style={css.header} data-testid="apps-header">
        <div style={css.titleWrap}>
          <div style={css.title} data-testid="apps-title">{pageTitle}</div>
          <div style={css.subCount} data-testid="apps-count">{T.count(total)}</div>
        </div>
        {canCreate && !archived && view !== "invoices" && view !== "follow_up" && (
          <button
            onClick={() => navigate("/applications/new")}
            className="ws-new-btn"
            style={css.newBtn}
            data-testid="new-application-btn"
          >
            <Plus size={16} strokeWidth={2.4} />
            <span>{T.new_app}</span>
          </button>
        )}
      </div>

      {/* ── Segmented tabs (Apple UISegmentedControl feel) — only when browsing normal Applications ── */}
      {tabs && (
        <div style={css.segWrap} role="tablist" data-testid="apps-tabs">
          {tabs.map(({ k, label }) => (
            <button
              key={k}
              onClick={() => { setView(k); exitSelect(); }}
              style={css.seg(view === k && !archived)}
              role="tab"
              aria-selected={view === k && !archived}
              data-testid={`tab-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* ── Search + tools ────────────────────────────────────── */}
      <div style={css.toolbar}>
        <div style={css.searchWrap}>
          <Search size={17} strokeWidth={2} style={css.searchIcon} />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={T.search_ph}
            className="ws-search"
            style={css.search}
            data-testid="apps-search"
          />
        </div>
        <button
          type="button"
          onClick={() => {/* future: open filters sheet */}}
          style={css.iconBtn(false)}
          data-testid="apps-filter"
          aria-label={lang === "ar" ? "فلترة" : "Filter"}
          title={lang === "ar" ? "فلترة" : "Filter"}
        >
          <SlidersHorizontal size={18} strokeWidth={1.75} />
        </button>
        {view === "invoices" && (
          <button
            type="button"
            onClick={() => setVerifyOpen(true)}
            style={{
              ...css.iconBtn(false),
              color: theme.gold,
              borderColor: theme.borderAccent,
              background: "rgba(212,175,55,0.10)",
            }}
            data-testid="invoice-verify-open"
            title={lang === "ar" ? "التحقق من فاتورة (QR)" : "Verify invoice (QR)"}
            aria-label={lang === "ar" ? "التحقق من فاتورة" : "Verify invoice"}
          >
            <ShieldCheck size={18} strokeWidth={2} />
          </button>
        )}
        {isRoot && (
          <button
            onClick={() => selectMode ? exitSelect() : setSelectMode(true)}
            style={css.iconBtn(selectMode)}
            data-testid="select-mode-toggle"
            aria-label={selectMode ? T.cancel_select : T.select_mode}
            title={selectMode ? T.cancel_select : T.select_mode}
          >
            {selectMode ? <X size={18} strokeWidth={2} /> : <CheckSquare size={18} strokeWidth={1.75} />}
          </button>
        )}
      </div>

      {verifyOpen && <InvoiceVerifyModal theme={theme} lang={lang} dir={dir} onClose={() => setVerifyOpen(false)} />}

      {selectMode && (
        <div style={css.bulkBar} data-testid="bulk-bar">
          <button onClick={toggleAll} style={css.bulkBtnGhost} data-testid="bulk-select-all">
            {selected.size === allIds.length && allIds.length > 0 ? <Square size={14} /> : <CheckSquare size={14} />}
            {selected.size === allIds.length && allIds.length > 0 ? T.deselect_all : T.select_all}
          </button>
          <span style={css.bulkCount}>{T.selected(selected.size)}</span>
          <div style={{ flex: 1 }} />
          {archived ? (
            <button onClick={() => doBulk("restore")} disabled={busyBulk || !selected.size} style={{ ...css.bulkBtnGhost, color: theme.gold, borderColor: theme.borderAccent }} data-testid="bulk-restore">
              {T.restore_btn}
            </button>
          ) : (
            <button onClick={() => doBulk("archive")} disabled={busyBulk || !selected.size} style={css.bulkBtnGhost} data-testid="bulk-archive">
              <Archive size={13} strokeWidth={1.75} /> {T.archive_btn}
            </button>
          )}
          <button onClick={() => doBulk("delete")} disabled={busyBulk || !selected.size} style={css.bulkBtnDanger} data-testid="bulk-delete">
            <Trash2 size={13} strokeWidth={1.75} /> {T.delete_btn}
          </button>
        </div>
      )}

      {err && <div style={css.err} data-testid="apps-error">{err}</div>}

      <div style={css.listCard} data-testid="apps-list">
        {loading ? (
          [0, 1, 2, 3, 4].map((i) => <div key={i} style={css.skRow} />)
        ) : items.length === 0 ? (
          <div style={css.empty} data-testid="apps-empty">
            {emptyMessage}
          </div>
        ) : (
          items.map((a, idx) => (
            <AppRow
              key={a.id} a={a} theme={theme} lang={lang} T={T} dir={dir}
              isDesktop={isDesktop}
              index={idx}
              isLast={idx === items.length - 1}
              onClick={() => selectMode ? toggle(a.id) : navigate(`/applications/${a.id}`)}
              selectMode={selectMode}
              selected={selected.has(a.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * Row: Apple grouped-list style
 *   • Avatar chip w/ initials
 *   • Name (semibold) + inline meta chips
 *   • Right-side semantic pills (travel / appt / late)
 *   • Left hairline accent: red=late, amber=warn, green=fresh
 *   • Freshness dot pulses (Notification style)
 *   • Cascading fade-in on mount
 * ──────────────────────────────────────────────────────────────── */
function AppRow({ a, theme, lang, T, dir, isDesktop, index, isLast, onClick, selectMode, selected }) {
  const statusName = statusLabel(a.current_status_code, lang);
  const country = localizeCountry(a.country, lang);
  const nowMs = Date.now();
  const daysToAppointment = a.appointment_date
    ? Math.round((new Date(a.appointment_date).getTime() - nowMs) / 86400000)
    : null;
  const isUrgent = daysToAppointment !== null && daysToAppointment <= 10 && daysToAppointment >= -1;
  const isLate = a.sla_state === "breached";
  const isWarn = a.sla_state === "warning";

  const submittedAt = a.submission_date || a.created_at || null;
  const elapsedMs = submittedAt ? nowMs - new Date(submittedAt).getTime() : 0;
  const elapsedMin = Math.floor(elapsedMs / 60000);
  const elapsedHrs = Math.floor(elapsedMs / 3600000);
  const isFresh = elapsedHrs < 2;
  const isMed   = elapsedHrs >= 2 && elapsedHrs < 24;
  const elapsedLabel = (() => {
    if (!submittedAt) return "";
    if (elapsedMin < 60) return lang === "ar" ? `منذ ${fmtNum(elapsedMin)} د` : `${fmtNum(elapsedMin)}m`;
    if (elapsedHrs < 24) return lang === "ar" ? `منذ ${fmtNum(elapsedHrs)} س` : `${fmtNum(elapsedHrs)}h`;
    const days = Math.floor(elapsedHrs / 24);
    return lang === "ar" ? `منذ ${fmtNum(days)} يوم` : `${fmtNum(days)}d`;
  })();

  const accent =
      isLate ? theme.danger
    : isWarn ? theme.warning
    : isFresh ? theme.success
    : null;

  const source = a.is_office_submission ? T.source_office : T.source_portal;
  const sourceColor = a.is_office_submission ? theme.gold : theme.info;

  const ChevronIcon = dir === "rtl" ? ChevronLeft : ChevronRight;
  const name = a.client?.full_name || "—";
  const initial = name.trim().charAt(0).toUpperCase() || "?";
  // Deterministic avatar color from name
  const avatarPalette = [theme.info, theme.gold, theme.success, "#8B5CF6", "#EC4899", "#14B8A6"];
  const hash = Array.from(name).reduce((s, c) => s + c.charCodeAt(0), 0);
  const avatarColor = avatarPalette[hash % avatarPalette.length];

  /* ── Choose ONE priority pill to show (avoid stacked clutter, esp on mobile) ── */
  let priorityPill = null;
  if (isLate) {
    priorityPill = { icon: Clock, text: T.late, color: theme.danger, bg: "rgba(220,38,38,0.12)", border: "rgba(220,38,38,0.22)" };
  } else if (isUrgent) {
    priorityPill = {
      icon: Plane,
      text: daysToAppointment === 0 ? T.today : (lang === "ar" ? `${fmtNum(Math.abs(daysToAppointment))} ي` : `${fmtNum(Math.abs(daysToAppointment))}d`),
      color: theme.danger, bg: "rgba(220,38,38,0.12)", border: "rgba(220,38,38,0.22)",
    };
  } else if (a.appointment_date) {
    priorityPill = { icon: CalendarDays, text: fmtDate(a.appointment_date), color: theme.info, bg: "rgba(59,130,246,0.12)", border: "rgba(59,130,246,0.22)" };
  }

  const rowStyle = {
    display: "flex", alignItems: "center", gap: isDesktop ? 14 : 11,
    padding: isDesktop ? "16px 20px" : "12px 14px",
    cursor: "pointer",
    background: isLate ? "rgba(220,38,38,0.035)" : "transparent",
    transition: `background 160ms cubic-bezier(.2,.8,.2,1)`,
    minHeight: isDesktop ? 82 : 66,
    textAlign: "start",
    fontFamily: "inherit",
    color: "inherit",
    width: "100%",
    border: 0,
    borderInlineStart: accent ? `3px solid ${accent}` : "3px solid transparent",
    borderBlockEnd: isLast ? "none" : `1px solid ${theme.borderSoft}`,
    position: "relative",
    animation: `ws-row-in ${240 + Math.min(index, 8) * 40}ms cubic-bezier(.2,.8,.2,1) both`,
  };

  const avatarSize = isDesktop ? 42 : 36;
  const nameSize = isDesktop ? 15 : 13.5;
  const metaSize = isDesktop ? 12 : 11;

  return (
    <button
      onClick={onClick}
      data-testid={`app-row-${a.application_number || a.id}`}
      style={rowStyle}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = isLate
          ? "rgba(220,38,38,0.07)"
          : (theme.name === "dark" ? "rgba(255,255,255,0.035)" : "rgba(0,0,0,0.025)");
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = isLate ? "rgba(220,38,38,0.035)" : "transparent";
      }}
    >
      {selectMode && (
        <span style={{
          width: 22, height: 22, borderRadius: 6,
          background: selected ? theme.goldGradient : "transparent",
          border: `1.5px solid ${selected ? "transparent" : theme.border}`,
          color: selected ? "#0A0F19" : theme.textMuted,
          display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          {selected ? <CheckSquare size={14} strokeWidth={2.4} /> : <Square size={14} strokeWidth={2} />}
        </span>
      )}

      {/* Avatar chip */}
      <div style={{
        width: avatarSize, height: avatarSize, borderRadius: isDesktop ? 12 : 10,
        background: `linear-gradient(135deg, ${avatarColor}CC, ${avatarColor}88)`,
        color: "#fff", fontWeight: 900, fontSize: isDesktop ? 15 : 13,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
        boxShadow: `0 4px 12px ${avatarColor}33`,
        letterSpacing: 0.5,
        position: "relative",
      }}>
        {initial}
        {isFresh && (
          <span
            aria-label="new"
            style={{
              position: "absolute",
              top: -2, insetInlineEnd: -2,
              width: 10, height: 10, borderRadius: "50%",
              background: theme.success,
              border: `2px solid ${theme.surface}`,
              animation: "ws-fresh-glow 1.8s ease-in-out infinite",
            }}
          />
        )}
      </div>

      {/* Main content — strict 2 line layout, never wraps */}
      <div style={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
        {/* Line 1 — name (+ inline priority pill on mobile) */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2, minWidth: 0 }}>
          <div style={{
            fontSize: nameSize, fontWeight: 700, color: theme.text,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            letterSpacing: "-0.1px", flex: 1, minWidth: 0,
          }}>
            {name}
          </div>
          {!isDesktop && priorityPill && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 3,
              padding: "2px 7px", borderRadius: 999,
              background: priorityPill.bg, color: priorityPill.color,
              fontSize: 10, fontWeight: 800, whiteSpace: "nowrap",
              border: `1px solid ${priorityPill.border}`,
              flexShrink: 0,
            }}>
              <priorityPill.icon size={9} strokeWidth={2.4} />
              {priorityPill.text}
            </span>
          )}
        </div>

        {/* Line 2 — compact meta, single line, ellipsis */}
        <div style={{
          fontSize: metaSize, color: theme.textMuted, lineHeight: 1.5,
          display: "flex", gap: 6, alignItems: "center",
          whiteSpace: "nowrap", overflow: "hidden", minWidth: 0,
        }}>
          <span dir="ltr" style={{ fontFamily: "SF Mono, ui-monospace, monospace", fontWeight: 700, color: theme.textSoft, flexShrink: 0 }}>
            {a.application_number || ""}
          </span>
          <span style={{ opacity: 0.35, flexShrink: 0 }}>·</span>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 3,
            padding: "1px 7px", borderRadius: 999,
            background: `${sourceColor}18`, color: sourceColor,
            fontSize: metaSize - 0.5, fontWeight: 700, flexShrink: 0,
          }}>
            {a.is_office_submission ? <Building2 size={9} strokeWidth={2.4} /> : <Globe2 size={9} strokeWidth={2.4} />}
            {source}
          </span>
          <span style={{
            padding: "1px 7px", borderRadius: 999,
            background: theme.name === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
            color: theme.textSoft, fontSize: metaSize - 0.5, fontWeight: 700,
            overflow: "hidden", textOverflow: "ellipsis",
            maxWidth: isDesktop ? "none" : 120,
            whiteSpace: "nowrap",
          }}>{statusName}</span>
          {isDesktop && country && (
            <>
              <span style={{ opacity: 0.35 }}>·</span>
              <span>{country}</span>
            </>
          )}
          {elapsedLabel && (
            <>
              <span style={{ opacity: 0.35, flexShrink: 0 }}>·</span>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 3,
                color: isFresh ? theme.success : isMed ? theme.warning : theme.textMuted,
                fontWeight: 700, flexShrink: 0,
              }}>
                <Clock size={10} strokeWidth={2.2} />
                {elapsedLabel}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Desktop only: assignee + right priority pill + chevron */}
      {isDesktop && (
        <>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, fontSize: 11, color: theme.textMuted, flexShrink: 0 }}>
            {a.assigned_to_name ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 700 }}>
                <UserCheck size={12} strokeWidth={2} /> {a.assigned_to_name}
              </span>
            ) : (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: theme.danger, fontWeight: 700 }}>
                <UserX size={12} strokeWidth={2} /> {T.unassigned}
              </span>
            )}
            {a.updated_at && (
              <span dir="ltr" style={{ fontFamily: "SF Mono, ui-monospace, monospace", opacity: 0.75 }}>
                {fmtDate(a.updated_at)}
              </span>
            )}
          </div>

          <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
            {priorityPill && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "5px 11px", borderRadius: 999,
                background: priorityPill.bg, color: priorityPill.color,
                fontSize: 11, fontWeight: 800, whiteSpace: "nowrap",
                border: `1px solid ${priorityPill.border}`,
              }}>
                <priorityPill.icon size={11} strokeWidth={2} />
                {priorityPill.text}
              </span>
            )}
            <ChevronIcon size={16} style={{ color: theme.textMuted, opacity: 0.6, marginInlineStart: 2 }} />
          </div>
        </>
      )}

      {/* Mobile: chevron only (priority pill already inline in name line) */}
      {!isDesktop && (
        <ChevronIcon size={15} style={{ color: theme.textMuted, opacity: 0.55, flexShrink: 0 }} />
      )}
    </button>
  );
}


/* ────────────────────────────────────────────────────────────────
 * InvoiceVerifyModal — merged from AdminInvoiceVerifyPage.
 * Type an invoice ref or application number → verify.
 * Uses the same `/api/admin/invoices/verify` endpoint.
 * ──────────────────────────────────────────────────────────────── */
function InvoiceVerifyModal({ theme, lang, dir, onClose }) {
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const scanContainerId = "vta-inv-verify-scan";

  const STATUS = {
    valid:     { icon: ShieldCheck, color: "#16a34a", bg: "rgba(22,163,74,0.12)",
                 label: lang === "ar" ? "فاتورة أصلية ومعتمدة" : "Valid & signed" },
    tampered:  { icon: ShieldAlert, color: "#dc2626", bg: "rgba(220,38,38,0.12)",
                 label: lang === "ar" ? "فاتورة معدّلة خارج النظام" : "Tampered outside the system" },
    cancelled: { icon: ShieldOff,   color: "#dc2626", bg: "rgba(220,38,38,0.10)",
                 label: lang === "ar" ? "ملغاة" : "Cancelled" },
    refunded:  { icon: ShieldOff,   color: "#d4af37", bg: "rgba(212,175,55,0.12)",
                 label: lang === "ar" ? "مستردّة" : "Refunded" },
    not_found: { icon: ShieldOff,   color: "#f59e0b", bg: "rgba(245,158,11,0.10)",
                 label: lang === "ar" ? "لم يتم العثور على الفاتورة" : "Invoice not found" },
  };

  const runVerify = async (q, providedHash) => {
    if (!q?.trim()) {
      setErr(lang === "ar" ? "أدخل رقم المعاملة أو الفاتورة" : "Enter application or invoice number");
      return;
    }
    setBusy(true); setErr(""); setResult(null);
    try {
      const isInv = q.toUpperCase().startsWith("INV-");
      const params = new URLSearchParams();
      if (isInv) params.set("ref", q); else params.set("application_number", q);
      if (providedHash) params.set("hash", providedHash);
      const { data } = await api.get(`/admin/invoices/verify?${params.toString()}`);
      setResult(data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || (lang === "ar" ? "تعذّر التحقق" : "Verification failed"));
    } finally { setBusy(false); }
  };

  const submit = (e) => { e?.preventDefault?.(); runVerify(input.trim()); };

  const stopScan = async () => {
    try { await scannerRef.current?.stop(); } catch { /* ignore */ }
    setScanning(false);
  };

  const startScan = async () => {
    setErr(""); setResult(null); setScanning(true);
    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode(scanContainerId);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          async (decoded) => {
            try { await scanner.stop(); } catch { /* ignore */ }
            setScanning(false);
            try {
              const u = new URL(decoded.startsWith("http") ? decoded : `https://x${decoded}`);
              const ref = u.searchParams.get("ref");
              const hash = u.searchParams.get("hash");
              const appn = u.searchParams.get("application_number");
              if (ref || appn) {
                setInput(ref || appn);
                await runVerify(ref || appn, hash);
              } else {
                setErr(lang === "ar" ? "رمز QR غير صالح — لا يحتوي على بيانات فاتورة" : "Invalid QR — no invoice payload");
              }
            } catch {
              setErr(lang === "ar" ? "رمز QR غير قابل للقراءة" : "Unreadable QR");
            }
          },
          () => { /* per-frame errors ignored */ }
        );
      } catch (ex) {
        setErr(`${lang === "ar" ? "تعذّر تشغيل الكاميرا" : "Camera failed"}: ${ex?.message || ex}`);
        setScanning(false);
      }
    }, 60);
  };

  useEffect(() => () => { stopScan(); }, []);

  const s = result ? (STATUS[result.status] || STATUS.not_found) : null;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
      data-testid="invoice-verify-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        dir={dir}
        style={{
          background: theme.surface,
          border: `1px solid ${theme.borderSoft}`,
          borderRadius: 18,
          width: "100%", maxWidth: 480,
          boxShadow: theme.sheetShadow,
          fontFamily: "'Tajawal','Segoe UI', -apple-system, sans-serif",
          overflow: "hidden",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px", borderBottom: `1px solid ${theme.borderSoft}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <ShieldCheck size={20} strokeWidth={2.2} color={theme.gold} />
            <div style={{ fontSize: 16, fontWeight: 800, color: theme.text }}>
              {lang === "ar" ? "التحقق من فاتورة" : "Verify Invoice"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 10, background: "transparent",
              border: `1px solid ${theme.borderSoft}`, color: theme.text, cursor: "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}
            data-testid="invoice-verify-close"
            aria-label={lang === "ar" ? "إغلاق" : "Close"}
          ><X size={16} strokeWidth={2} /></button>
        </div>

        <form onSubmit={submit} style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: theme.textMuted }}>
            {lang === "ar" ? "رقم الفاتورة أو المعاملة" : "Invoice or application number"}
          </label>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={lang === "ar" ? "INV-… أو VTA-…" : "INV-… or VTA-…"}
            style={{
              padding: "12px 14px", borderRadius: 10,
              background: theme.inputBg, border: `1px solid ${theme.inputBorder}`,
              color: theme.text, fontSize: 14,
              fontFamily: "SF Mono, ui-monospace, monospace",
              direction: "ltr", textAlign: dir === "rtl" ? "right" : "left",
              outline: "none",
            }}
            data-testid="invoice-verify-input"
            autoFocus
          />

          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="submit"
              disabled={busy || !input.trim()}
              style={{
                flex: 1,
                padding: "11px 16px", borderRadius: 10,
                background: theme.goldGradient, color: "#0A0F19",
                border: "none", fontWeight: 800, fontSize: 14, cursor: "pointer",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                opacity: busy || !input.trim() ? 0.55 : 1,
                fontFamily: "inherit",
              }}
              data-testid="invoice-verify-submit"
            >
              {busy && <Loader2 size={14} className="ws-spin" />}
              <ShieldCheck size={15} strokeWidth={2.2} />
              {lang === "ar" ? "تحقّق" : "Verify"}
            </button>
            <button
              type="button"
              onClick={scanning ? stopScan : startScan}
              disabled={busy}
              style={{
                padding: "11px 16px", borderRadius: 10,
                background: scanning ? "rgba(220,38,38,0.10)" : theme.surfaceElevated,
                color: scanning ? "#dc2626" : theme.text,
                border: `1px solid ${scanning ? "rgba(220,38,38,0.35)" : theme.border}`,
                fontWeight: 800, fontSize: 14, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
                fontFamily: "inherit",
              }}
              data-testid="invoice-verify-scan"
            >
              <QrCode size={16} strokeWidth={2.2} />
              {scanning
                ? (lang === "ar" ? "إيقاف" : "Stop")
                : (lang === "ar" ? "مسح QR" : "Scan QR")}
            </button>
          </div>

          {scanning && (
            <div
              id={scanContainerId}
              data-testid="invoice-verify-scan-frame"
              style={{
                width: "100%", aspectRatio: "1/1",
                borderRadius: 14,
                background: "#000",
                border: `2px dashed ${theme.gold}`,
                overflow: "hidden",
              }}
            />
          )}
          {scanning && (
            <div style={{ fontSize: 11, color: theme.textMuted, textAlign: "center" }}>
              {lang === "ar" ? "وجّه الكاميرا نحو رمز QR المطبوع على الإيصال" : "Point the camera at the printed invoice QR"}
            </div>
          )}

          {err && (
            <div data-testid="invoice-verify-error" style={{
              padding: "10px 12px", borderRadius: 10,
              background: theme.errorBg, border: `1px solid ${theme.errorBorder}`,
              color: theme.errorText, fontSize: 13, lineHeight: 1.5,
            }}>{err}</div>
          )}

          {result && s && (
            <div data-testid="invoice-verify-result" style={{
              padding: "14px 16px", borderRadius: 12,
              background: s.bg, border: `1px solid ${s.color}44`,
              display: "flex", alignItems: "flex-start", gap: 10,
            }}>
              <s.icon size={22} strokeWidth={2.2} color={s.color} style={{ flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ color: s.color, fontWeight: 800, fontSize: 14, marginBottom: 4 }}>
                  {s.label}
                </div>
                {result.invoice && (
                  <div style={{ fontSize: 12, color: theme.textSoft, lineHeight: 1.7 }}>
                    <div dir="ltr" style={{ fontFamily: "SF Mono, ui-monospace, monospace" }}>
                      {result.invoice.reference || result.invoice.application_number}
                    </div>
                    {result.invoice.total_amount != null && (
                      <div>
                        {lang === "ar" ? "الإجمالي" : "Total"}: {fmtNum(result.invoice.total_amount)} SAR
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </form>
        <style>{`
          .ws-spin { animation: ws-spin 0.9s linear infinite; }
          @keyframes ws-spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    </div>
  );
}
